import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { RiCloseLine, RiZoomInLine, RiZoomOutLine, RiRestartLine } from "react-icons/ri";
import Typography from "../Typography";
import Button from "../Button/Button";
import { clampWidthPercent } from "./descriptionImageUtils";

const WIDTH_PRESETS = [25, 50, 75, 100];
const ZOOM_MIN = 50;
const ZOOM_MAX = 400;
const ZOOM_STEP = 10;

function clampPreviewZoom(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 100;
  return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round(n / ZOOM_STEP) * ZOOM_STEP));
}

export default function DescriptionImageSizeModal({
  isOpen,
  src,
  widthPercent = 100,
  onClose,
  onApply,
  /** View-only: hides the width slider/presets and Apply — just zoomable preview + Close. */
  readOnly = false,
}) {
  const [draftWidth, setDraftWidth] = useState(widthPercent);
  const [previewZoom, setPreviewZoom] = useState(100);
  const previewRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setDraftWidth(clampWidthPercent(widthPercent));
      setPreviewZoom(100);
    }
  }, [isOpen, widthPercent]);

  const adjustZoom = useCallback((delta) => {
    setPreviewZoom((current) => clampPreviewZoom(current + delta));
  }, []);

  useEffect(() => {
    const node = previewRef.current;
    if (!isOpen || !node) return undefined;

    const handleWheel = (event) => {
      event.preventDefault();
      const delta = event.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
      setPreviewZoom((current) => clampPreviewZoom(current + delta));
    };

    node.addEventListener("wheel", handleWheel, { passive: false });
    return () => node.removeEventListener("wheel", handleWheel);
  }, [isOpen]);

  if (!isOpen || !src) return null;
  if (typeof document === "undefined") return null;

  const safeWidth = clampWidthPercent(draftWidth);
  const zoomFactor = previewZoom / 100;

  return createPortal(
    <div
      className="fixed inset-0 z-[10050] flex items-center justify-center p-3 sm:p-4 bg-black/55"
      role="dialog"
      aria-modal="true"
      aria-labelledby="description-image-preview-title"
      onClick={onClose}
    >
      <div
        className="bg-surface-primary rounded-lg shadow-xl w-full max-w-6xl flex flex-col max-h-[95vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-border-primary shrink-0">
          <Typography
            id="description-image-preview-title"
            variant="h5"
            className="font-semibold text-text-primary"
          >
            Image preview
          </Typography>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onClose}
            className="!border-0 !shadow-none !bg-transparent !p-0 !min-w-0"
            aria-label="Close"
          >
            <RiCloseLine className="w-5 h-5 text-text-secondary" />
          </Button>
        </div>

        <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
          <div
            ref={previewRef}
            className="flex-1 min-h-[min(58vh,520px)] overflow-auto p-5"
            title="Scroll to zoom"
          >
            <div className="w-full min-h-[min(55vh,500px)] flex items-center justify-center rounded-lg border border-border-primary bg-[var(--bg-secondary)] p-6">
              <img
                src={src}
                alt="Preview"
                className="object-contain block mx-auto"
                style={{
                  width: `${safeWidth * zoomFactor}%`,
                  height: "auto",
                  maxWidth: "none",
                  maxHeight: `${Math.round(760 * zoomFactor)}px`,
                }}
                draggable={false}
              />
            </div>
          </div>

          <div className="shrink-0 px-5 py-4 border-t border-border-primary bg-surface-primary space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
              <Typography
                variant="label"
                className="sm:w-28 shrink-0 text-sm font-medium text-text-primary"
              >
                Zoom: {previewZoom}%
              </Typography>
              <div className="flex flex-wrap items-center gap-2 flex-1">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  className="!px-2.5 !py-1.5"
                  onClick={() => adjustZoom(-ZOOM_STEP)}
                  disabled={previewZoom <= ZOOM_MIN}
                  aria-label="Zoom out"
                >
                  <RiZoomOutLine className="w-4 h-4" />
                </Button>
                <input
                  type="range"
                  min={ZOOM_MIN}
                  max={ZOOM_MAX}
                  step={ZOOM_STEP}
                  value={previewZoom}
                  onChange={(e) => setPreviewZoom(clampPreviewZoom(Number(e.target.value)))}
                  className="flex-1 w-full min-w-[8rem] accent-brand-primary h-2 cursor-pointer"
                  aria-label="Preview zoom"
                />
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  className="!px-2.5 !py-1.5"
                  onClick={() => adjustZoom(ZOOM_STEP)}
                  disabled={previewZoom >= ZOOM_MAX}
                  aria-label="Zoom in"
                >
                  <RiZoomInLine className="w-4 h-4" />
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  className="!px-2.5 !py-1.5"
                  onClick={() => setPreviewZoom(100)}
                  disabled={previewZoom === 100}
                  aria-label="Reset zoom"
                  title="Reset zoom"
                >
                  <RiRestartLine className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <Typography variant="text2" className="text-text-secondary text-xs">
              {readOnly
                ? "Scroll over the preview to zoom in or out."
                : "Scroll over the preview to zoom in or out. Width controls how the image appears in the description."}
            </Typography>

            {!readOnly && (
              <>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                  <Typography
                    variant="label"
                    className="sm:w-28 shrink-0 text-sm font-medium text-text-primary"
                  >
                    Width: {safeWidth}%
                  </Typography>
                  <input
                    type="range"
                    min={10}
                    max={100}
                    step={5}
                    value={safeWidth}
                    onChange={(e) => setDraftWidth(clampWidthPercent(Number(e.target.value)))}
                    className="flex-1 w-full min-w-0 accent-brand-primary h-2 cursor-pointer"
                    aria-label="Image width in description"
                  />
                </div>

                <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2">
                  {WIDTH_PRESETS.map((preset) => (
                    <Button
                      key={preset}
                      type="button"
                      variant={safeWidth === preset ? "primary" : "secondary"}
                      size="sm"
                      className="!min-w-[3.25rem] !px-3 !py-1.5"
                      onClick={() => setDraftWidth(preset)}
                    >
                      {preset}%
                    </Button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 px-5 py-4 border-t border-border-primary shrink-0">
          {readOnly ? (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={onClose}
              className="btn-reset"
            >
              Close
            </Button>
          ) : (
            <>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={onClose}
                className="btn-reset"
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="primary"
                size="sm"
                className="!px-4 !py-1.5"
                onClick={() => onApply(safeWidth)}
              >
                Apply
              </Button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
